import { message, PageHeader, Spin, Upload, Tag, Tabs } from "antd";
import React, { Component } from "react";
import { connect } from "react-redux";
import axios from "axios";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import { ButtonItem, TableItem } from "../../../../Components/Items";
import {
  InboxOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  DownloadOutlined,
} from "@ant-design/icons";

const { Dragger } = Upload;
const { TabPane } = Tabs;

const EXTENSIONES_IMAGEN = ["jpg", "jpeg", "png"];

function esImagen(nombre) {
  const ext = nombre.split(".").pop().toLowerCase();
  return EXTENSIONES_IMAGEN.includes(ext);
}

function normalizar(str) {
  if (!str) return "";
  return str.trim().toLowerCase();
}

function buscarCoincidencia(nombreSinExt, insumos) {
  const n = normalizar(nombreSinExt);
  for (const ins of insumos) {
    if (normalizar(ins.clave) === n)
      return { insumo: ins, campo: "Clave", valor: ins.clave };
    if (normalizar(ins.codigo) === n)
      return { insumo: ins, campo: "Código", valor: ins.codigo };
    if (ins.codigoBarras && normalizar(ins.codigoBarras) === n)
      return { insumo: ins, campo: "Cód. Barras", valor: ins.codigoBarras };
    if (ins.codigoProveedor && normalizar(ins.codigoProveedor) === n)
      return { insumo: ins, campo: "Cód. Proveedor", valor: ins.codigoProveedor };
  }
  return null;
}

async function extraerImagenes(zip) {
  const imagenes = [];
  zip.forEach((rutaRelativa, archivo) => {
    if (!archivo.dir && esImagen(rutaRelativa)) {
      imagenes.push({ rutaRelativa, archivo });
    }
  });
  return imagenes;
}

class SubirImagenesInsumos extends Component {
  state = {
    loading: false,
    todosInsumos: [],
    archivoZip: null,
    encontradas: [],
    noEncontradas: [],
  };

  componentDidMount() {
    this.setState({ loading: true });
    axios
      .post(
        "insumos/selectInsumosRaw",
        { empresaId: this.props.empresaId },
        { headers: this.props.headersToken }
      )
      .then((res) => {
        if (res.data.success === 2) {
          this.setState({ todosInsumos: res.data.insumos, loading: false });
        } else {
          message.error("No se pudieron cargar los insumos");
          this.setState({ loading: false });
        }
      })
      .catch(() => {
        message.error("Error al cargar insumos");
        this.setState({ loading: false });
      });
  }

  atras() {
    this.props.history.goBack();
  }

  async procesarZip(file) {
    const MB_70 = 70 * 1024 * 1024;
    if (file.size > MB_70) {
      message.error(
        "El archivo supera los 70 MB. Por favor súbelo por partes."
      );
      this.setState({ archivoZip: null });
      return;
    }

    this.setState({ loading: true, encontradas: [], noEncontradas: [] });
    try {
      const zip = await JSZip.loadAsync(file);
      const imagenes = await extraerImagenes(zip);

      if (imagenes.length === 0) {
        message.warning("El ZIP no contiene imágenes (.jpg, .jpeg, .png)");
        this.setState({ loading: false });
        return;
      }

      if (imagenes.length > 100) {
        message.error(
          `El ZIP contiene ${imagenes.length} imágenes. El límite es 100. Por favor súbelas por partes.`
        );
        this.setState({ loading: false, archivoZip: null });
        return;
      }

      const encontradas = [];
      const noEncontradas = [];

      imagenes.forEach(({ rutaRelativa, archivo }) => {
        const nombreArchivo = rutaRelativa.split("/").pop();
        const nombreSinExt = nombreArchivo.replace(/\.[^/.]+$/, "");
        const resultado = buscarCoincidencia(nombreSinExt, this.state.todosInsumos);
        const base = { key: rutaRelativa, nombreArchivo, rutaRelativa, archivoJszip: archivo };

        if (resultado) {
          encontradas.push({ ...base, insumo: resultado.insumo, campo: resultado.campo, valor: resultado.valor });
        } else {
          noEncontradas.push(base);
        }
      });

      this.setState({ encontradas, noEncontradas, loading: false });
    } catch {
      message.error("Error al leer el ZIP");
      this.setState({ loading: false });
    }
  }

  customRequest(e) {
    const file = e.file;
    const esZip =
      file.type === "application/zip" ||
      file.type === "application/x-zip-compressed" ||
      file.name.toLowerCase().endsWith(".zip");

    if (!esZip) {
      message.error(`${file.name} no es un archivo ZIP`);
      e.onError("Tipo inválido");
      return;
    }

    this.setState({ archivoZip: file, encontradas: [], noEncontradas: [] });
    e.onSuccess("ok");
    this.procesarZip(file);
  }

  onRemove() {
    this.setState({ archivoZip: null, encontradas: [], noEncontradas: [] });
  }

  async subirZip() {
    const { encontradas } = this.state;
    if (encontradas.length === 0) return;

    this.setState({ loading: true });
    try {
      const nuevoZip = new JSZip();
      const manifest = {};
      for (const fila of encontradas) {
        const contenido = await fila.archivoJszip.async("uint8array");
        nuevoZip.file(fila.nombreArchivo, contenido);
        manifest[fila.nombreArchivo] = fila.insumo._id;
      }
      nuevoZip.file("manifest.json", JSON.stringify(manifest));
      const blob = await nuevoZip.generateAsync({ type: "blob" });

      const fd = new FormData();
      fd.append("empresaId", this.props.empresaId);
      fd.append("zip", blob, "imagenes_insumos.zip");

      const res = await axios.post("insumos/subirImagenesZip", fd, {
        headers: this.props.headersToken,
      });

      this.setState({ loading: false });

      if (res.data.success === 2) {
        const { procesados = 0, descartados = 0 } = res.data;
        message.success(`Se procesaron ${procesados} imagen(es)`);
        if (descartados > 0) message.warning(`${descartados} imagen(es) descartadas por el servidor`);
        this.setState({ archivoZip: null, encontradas: [], noEncontradas: [] });
      } else {
        message.error(res.data.err || "Error al subir imágenes");
      }
    } catch {
      this.setState({ loading: false });
      message.error("Error al generar o enviar el ZIP");
    }
  }

  async descargarNoEncontradasZip() {
    const { noEncontradas } = this.state;
    if (noEncontradas.length === 0) return;

    const zip = new JSZip();
    for (const fila of noEncontradas) {
      const contenido = await fila.archivoJszip.async("uint8array");
      zip.file(fila.nombreArchivo, contenido);
    }
    const blob = await zip.generateAsync({ type: "blob" });
    saveAs(blob, "no_encontradas.zip");
  }

  descargarListaTxt() {
    const { noEncontradas } = this.state;
    if (noEncontradas.length === 0) return;

    const texto = noEncontradas
      .map((f) => `${f.nombreArchivo}\t${f.rutaRelativa}`)
      .join("\n");
    const blob = new Blob([texto], { type: "text/plain;charset=utf-8" });
    saveAs(blob, "no_encontradas.txt");
  }

  render() {
    const { loading, archivoZip, encontradas, noEncontradas } = this.state;
    const hayResultados = encontradas.length > 0 || noEncontradas.length > 0;

    const columnasEncontradas = [
      {
        title: "Archivo",
        dataIndex: "nombreArchivo",
        key: "nombreArchivo",
        buscar: "nombreArchivo",
      },
      {
        title: "Descripción",
        key: "descripcion",
        render: (_, f) => f.insumo?.descripcion || "—",
      },
      {
        title: "Coincidencia",
        key: "campo",
        width: 200,
        render: (_, f) => (
          <Tag color="blue">
            {f.campo}: {f.valor}
          </Tag>
        ),
      },
      {
        title: "Estado",
        key: "estado",
        width: 120,
        render: () => (
          <Tag icon={<CheckCircleOutlined />} color="success">
            Se sube
          </Tag>
        ),
      },
    ];

    const columnasNoEncontradas = [
      {
        title: "Archivo",
        dataIndex: "nombreArchivo",
        key: "nombreArchivo",
        buscar: "nombreArchivo",
      },
      {
        title: "Ruta en ZIP",
        dataIndex: "rutaRelativa",
        key: "rutaRelativa",
        render: (texto) => <span style={{ color: "#aaa" }}>{texto}</span>,
      },
      {
        title: "Estado",
        key: "estado",
        width: 120,
        render: () => (
          <Tag icon={<CloseCircleOutlined />} color="error">
            Descartada
          </Tag>
        ),
      },
    ];

    return (
      <>
        <PageHeader
          onBack={this.atras.bind(this)}
          title="Subir Imágenes por ZIP"
          style={{
            border: "1px solid rgb(235, 237, 240)",
            backgroundColor: "white",
            marginBottom: 10,
          }}
        />
        <div className="card-background" style={{ paddingTop: 5 }}>
          <Spin spinning={loading} tip="Cargando...">
            <Dragger
              accept=".zip,application/zip,application/x-zip-compressed"
              maxCount={1}
              fileList={
                archivoZip
                  ? [{ uid: "zip", name: archivoZip.name, status: "done" }]
                  : []
              }
              customRequest={this.customRequest.bind(this)}
              onRemove={() => this.onRemove()}
              style={{ marginTop: 20, marginBottom: 24 }}
              disabled={loading}
            >
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">
                Arrastra un ZIP aquí o haz clic para seleccionarlo
              </p>
              <p className="ant-upload-hint">
                Formatos: .jpg, .jpeg, .png — se busca en todas las subcarpetas.
                El nombre del archivo debe coincidir con la clave, código, cód.
                de barras o cód. de proveedor del insumo.
              </p>
            </Dragger>

            <div style={{ marginBottom: 20, fontSize: 12, color: '#8c8c8c', lineHeight: 1.4 }}>
              <strong>Recomendación de imagen:</strong> usa imágenes <strong>cuadradas (1:1), 1200×1200&nbsp;px</strong>,
              fondo blanco o transparente y el producto centrado, para que todos los productos
              se vean parejos en la tienda online. Formatos: JPG/PNG, máx. 5&nbsp;MB.
            </div>

            {hayResultados && (
              <Tabs defaultActiveKey="noEncontradas">
                <TabPane
                  tab={`Sin coincidencia (${noEncontradas.length})`}
                  key="noEncontradas"
                >
                  {noEncontradas.length > 0 ? (
                    <>
                      <TableItem
                        size="small"
                        dataSource={noEncontradas}
                        columns={columnasNoEncontradas}
                        pagination={{ pageSize: 20, hideOnSinglePage: true }}
                        style={{ marginBottom: 16 }}
                      />
                      <ButtonItem
                        icon={<DownloadOutlined />}
                        onClick={() => this.descargarNoEncontradasZip()}
                        style={{ marginRight: 8 }}
                      >
                        Descargar ZIP
                      </ButtonItem>
                      <ButtonItem
                        icon={<DownloadOutlined />}
                        onClick={() => this.descargarListaTxt()}
                      >
                        Descargar lista (.txt)
                      </ButtonItem>
                    </>
                  ) : (
                    <p style={{ color: "#aaa", padding: "16px 0" }}>
                      Todas las imágenes coincidieron con un insumo.
                    </p>
                  )}
                </TabPane>

                <TabPane
                  tab={`Para subir (${encontradas.length})`}
                  key="encontradas"
                >
                  {encontradas.length > 0 ? (
                    <>
                      <TableItem
                        size="small"
                        dataSource={encontradas}
                        columns={columnasEncontradas}
                        pagination={{ pageSize: 20, hideOnSinglePage: true }}
                        style={{ marginBottom: 16 }}
                      />
                      <ButtonItem
                        type="primary"
                        onClick={() => this.subirZip()}
                        style={{ display: "block", marginLeft: "auto" }}
                      >
                        Subir {encontradas.length} imagen
                        {encontradas.length !== 1 ? "es" : ""}
                      </ButtonItem>
                    </>
                  ) : (
                    <p style={{ color: "#aaa", padding: "16px 0" }}>
                      Ninguna imagen coincide con un insumo.
                    </p>
                  )}
                </TabPane>
              </Tabs>
            )}
          </Spin>
        </div>
      </>
    );
  }
}

const mapStateToProps = (state) => ({
  headersToken: state.user.headersToken,
  empresaId: state.empresa.id,
});

export default connect(mapStateToProps)(SubirImagenesInsumos);
